import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const reports = {
    // Stack header titles (app/reports/_layout.tsx).
    screens: {
        index: 'Reports',
        custom: 'Report',
        advanced: 'Advanced reports',
        analysis: 'Analysis',
        categoryNet: 'Result by category',
        compare: 'Compare periods',
        weekday: 'By day of week',
        fixedVariable: 'Fixed × variable',
        cashFlow: 'Cash flow',
        budgetVsActual: 'Budgeted × actual',
        export: 'Export to spreadsheet',
        invoices: 'Statement report',
    },

    // Export error toasts, reused by almost every screen.
    errors: {
        export: 'Export error',
        exportFailed: 'Export failed',
    },

    // ReportPeriodSelector (shared by all report screens) + the
    // getReportPeriodRange helper (outside a component → i18n.t at use time).
    period: {
        month: 'Month',
        year: 'Year',
        custom: 'Custom',
        until: 'to',
        customRangeLabel: '{{start}} to {{end}}',
        status: 'Status',
        statusAll: 'All',
        statusSettled: 'Cleared',
        statusPending: 'Pending',
        statusAllHint: 'Cleared + upcoming',
        statusSettledHint: 'Only what already happened',
        statusPendingHint: 'Only what is still to happen',
    },

    // ReportPdfButton.
    pdfButton: {
        exportPdf: 'Export PDF',
    },

    // app/reports/advanced.tsx menu (REPORTS constant → getReports(t) function).
    teaser: {
        badge: 'PRO',
        cta: 'Unlock with Pro',
        dismiss: 'Not now',
        invoicesTitle: 'Your invoice, broken down by card, tag and category',
        invoicesSubtitle: 'The report is already built from your entries. Unlock to see the amounts.',
        cashFlowTitle: 'Where your cash is heading through the period',
        cashFlowSubtitle: 'Income, expenses and the running total month by month, with your real numbers.',
        categoryNetTitle: 'What each category leaves in your pocket',
        categoryNetSubtitle: 'Income and expenses by category, with the net result of each one.',
        compareTitle: 'This period against the previous one, side by side',
        compareSubtitle: 'Income, expenses and what changed between the two periods.',
        weekdayTitle: 'Which weekday your money leaves on',
        weekdaySubtitle: 'Your spending summed by weekday, over the period you choose.',
        fixedVariableTitle: 'How much of your spending is fixed, how much is choice',
        fixedVariableSubtitle: 'Fixed and variable split apart, with the share of each.',
        budgetVsActualTitle: 'Budgeted against actual, month by month',
        budgetVsActualSubtitle: 'What you planned, what you spent and what was left in each budget.',
        exportTitle: 'Your entries as a spreadsheet',
        exportSubtitle: 'CSV ready for Excel and Google Sheets, with a monthly summary per account.',
    },
    advanced: {
        categoryNet: {
            title: 'Result by category',
            subtitle: 'Inflows, outflows and balance by period',
        },
        compare: {
            title: 'Compare periods',
            subtitle: 'Month, year or custom range',
        },
        weekday: {
            title: 'Expenses by day of week',
            subtitle: 'Which days you spend most in the period',
        },
        fixedVariable: {
            title: 'Fixed × variable',
            subtitle: 'Recurring vs one-off spending by period',
        },
        cashFlow: {
            title: 'Cash flow',
            subtitle: 'Inflows, outflows and cumulative balance month by month',
        },
        budgetVsActual: {
            title: 'Budgeted × actual',
            subtitle: 'Budgeted vs spent, month by month',
        },
        export: {
            title: 'Export to spreadsheet',
            subtitle: 'Monthly summary by account or all transactions (CSV)',
        },
        invoices: {
            title: 'Statement report',
            subtitle: 'Totals by tag, card or category across the month\'s statements',
        },
    },

    // app/reports/invoices.tsx
    invoices: {
        cardsLabel: 'Cards',
        categoriesLabel: 'Categories',
        tagsLabel: 'Tags',
        allCards: 'All cards',
        allCategories: 'All categories',
        allTags: 'All tags',
        noCategories: 'No categories in this month\'s statements.',
        cardsSheetTitle: 'Filter by card',
        cardsSheetSubtitle: 'Pick one or more. With none selected, we show all.',
        cardsSearchPlaceholder: 'Search card...',
        groupByLabel: 'Group by',
        totalFiltered: 'Filtered total',
        filterCardsMulti: '{{n}} cards',
        filterCategoriesMulti: '{{n}} categories',
        filterTagsMulti: '{{n}} tags',
        byTag: 'Tags',
        byCard: 'Cards',
        byCategory: 'Categories',
        totalLabel: 'Statements total',
        itemsLabel: 'Items',
        untagged: 'Untagged',
        uncategorized: 'Uncategorized',
        itemCount_one: '{{count}} item',
        itemCount_other: '{{count}} items',
        empty: 'No statements with entries this month for the chosen cards.',
        noCards: 'No credit cards yet. Create a credit card to use this report.',
        pdfTitle: 'Statement report',
        pdfColGroup: 'Group',
        pdfColItems: 'Items',
        pdfColAmount: 'Amount',
        exportPdf: 'Export PDF',
    },

    // app/reports/category-net.tsx
    categoryNet: {
        pdfTitle: 'Result by category',
        income: 'Inflows',
        expense: 'Outflows',
        result: 'Result',
        categoriesSection: 'Categories',
        colCategory: 'Category',
        empty: 'No transactions in this period.',
        rowInflow: 'in {{amount}}',
        rowOutflow: 'out {{amount}}',
    },

    // app/reports/compare.tsx
    compare: {
        pdfTitle: 'Compare periods',
        vs: '{{a}} vs {{b}}',
        variation: 'Change',
        summarySection: 'Summary',
        indicator: 'Indicator',
        category: 'Category',
        byCategory: 'Expenses by category',
        categoryEmpty: 'No expenses to compare in this period.',
        rows: {
            inflow: 'Inflows',
            outflow: 'Outflows',
            result: 'Result',
        },
        delta: {
            noChange: 'No change',
        },
    },

    // app/reports/weekday.tsx
    weekday: {
        pdfTitle: 'Expenses by day of week',
        totalInPeriod: 'Total in period',
        totalInPeriodLine: 'Total in period: {{amount}}',
        daysSection: 'Days of week',
        colDay: 'Day',
        colValue: 'Amount',
        colPctOfMax: '% of top day',
        pct: '{{pct}}%',
        days: {
            sunday: 'Sunday',
            monday: 'Monday',
            tuesday: 'Tuesday',
            wednesday: 'Wednesday',
            thursday: 'Thursday',
            friday: 'Friday',
            saturday: 'Saturday',
        },
    },

    // app/reports/fixed-variable.tsx. The PDF title uses a lowercase "x"
    // (matching today's screen literal), unlike the "×" in the headers/menu.
    fixedVariable: {
        pdfTitle: 'Fixed x variable',
        total: 'Total',
        fixed: 'Fixed',
        fixedSub: 'Recurring and subscriptions',
        variable: 'Variable',
        variableSub: 'One-off spending',
        expensesSection: 'Expenses',
        colType: 'Type',
        colDescription: 'Description',
        colValue: 'Amount',
        colPct: '%',
        pct: '{{pct}}%',
    },

    // app/reports/cash-flow.tsx
    cashFlow: {
        pdfTitle: 'Cash flow',
        pdfSubtitle: '{{range}} · actual (confirmed)',
        income: 'Inflows',
        expense: 'Outflows',
        net: 'Net',
        openNoSettle: 'Still to come',
        monthSection: 'Month by month',
        colMonth: 'Month',
        colIncome: 'Inflows',
        colExpense: 'Outflows',
        colNet: 'Net',
        colCumulative: 'Cumulative',
        colOpen: 'Open',
        legendNet: 'Monthly net',
        legendCumulative: 'Cumulative',
        pendingSummaryReceiveSuffix: ' · {{amount}} to receive',
        pendingNote: 'Pending items don\'t count in the actual above.',
        realizedSoFar: 'Actual so far',
        realizedSoFarLine: 'Actual so far: {{amount}}',
        noteProjected: 'Cash view including upcoming: counts what is still unpaid and the recurrences due within the period. Card purchases land on the invoice due date.',
        monthFlow: 'In {{income}} · Out {{expense}}',
        monthOpen: 'Open: {{parts}}',
        toPay: '{{amount}} to pay',
        toReceive: '{{amount}} to receive',
        cumulativeShort: 'total {{amount}}',
        note: 'Cash view: only confirmed transactions count in the actual; card purchases enter when the statement is paid. Unsettled transactions appear as open.',

        hero: {
            endsWith: {
                month: 'If nothing changes, you end the month with',
                year: 'If nothing changes, you end the year with',
                custom: 'If nothing changes, you end the period with',
            },
            settledLabel: 'Actual so far',
            pendingLabel: 'What is still to happen',
            deltaFromToday: '{{amount}} from what you have today',
            today: 'today',
            chipProjected: 'With upcoming',
            chipSettled: 'Cleared only',
            chipPending: 'Pending only',
        },

        critical: {
            title: '{{month}} is the month that drains the most',
            titlePeriod: '{{range}} drains the most',
            subtitle: '{{amount}} · {{reason}}',
            reasonPair: '{{first}} and {{second}} land together',
            reasonSingle: '{{first}} drives the month',
            reasonPlain: 'largest negative balance in the period',
        },

        months: {
            title: 'What each month does to your cash',
            legendProjected: 'upcoming',
            legendUnit: 'values in {{symbol}}',
            colOut: 'out',
            colIn: 'in',
            colNet: 'net',
            totalIn: 'In',
            totalOut: 'Out',
        },

        open: {
            prefix: 'Still to come:',
            join: ' and ',
            counted: 'Counted in the curve.',
            notCounted: 'Outside this view.',
        },
    },

    // app/reports/budget-vs-actual.tsx
    budgetVsActual: {
        pdfTitle: 'Budgeted × actual',
        budgeted: 'Budgeted',
        spent: 'Actual',
        diff: 'Difference',
        period: 'Period',
        leftover: '{{amount}} left',
        over: 'Over by {{amount}}',
        spentOfBudgeted: '{{spent}} of {{budgeted}}',
        monthProgress: '{{spent}} of {{budgeted}} · {{pct}}%',
        // Literal suffix concatenated to the formatted month (date-fns) when the
        // month is partial; kept as a suffix to avoid reworking the date format.
        partialSuffix: ' (partial)',
        monthSection: 'Month by month',
        colMonth: 'Month',
        colBudgeted: 'Budgeted',
        colSpent: 'Actual',
        colDiff: 'Difference',
        colPct: '%',
        pct: '{{pct}}%',
        budgetsSection: 'Budgets in period',
        colBudget: 'Budget',
        budgetsEmpty: 'No active budgets.',
        budgetFallbackName: 'Budget',
        emptyTitle: 'No budgets yet',
        emptyBody: 'Budgeted comes from your budgets. Create one in More › Budget and come back to compare with actual.',
        note: 'Budgeted uses the current budget caps, one cap per cycle that falls in the month (4 or 5 weeks, 2 fortnights; partial periods count the fraction of the month). Spent follows each budget\'s own cycle. Budget changes also apply to past months.',
    },

    // app/reports/export.tsx
    export: {
        period: 'Period',
        pdfTitle: 'PDF report for the period',
        pdfSubtitle: 'Financial summary with accounts, categories, months and transactions.',
        sheetTitle: 'Monthly spreadsheet by account',
        sheetSubtitle: 'Income and expenses by account. Uses the months covered by the selected period.',
        transactionsTitle: 'All transactions',
        transactionsSubtitle: 'One row per transaction, with description, category, account and card.',
        pdfReady: 'PDF ready',
        pdfDocTitle: 'Financial report {{range}}',
        sheetReady: 'Spreadsheet ready',
        // {{rowCount}} instead of {{count}}: literal number, "sources" is fixed (no plural).
        sheetReadyBody: '{{rowCount}} sources prepared for local sharing.',
        csvReady: 'CSV ready',
        csvReadyBody: '{{rowCount}} transactions prepared for local sharing.',
        note: 'CSVs use a ";" separator (pt-BR standard) and open directly in Excel or Google Sheets. The PDF uses the same local sharing. Nothing leaves your device unless you choose where to share.',
    },

    // app/reports/custom.tsx
    custom: {
        clear: 'Clear',
        period: 'Period',
        type: 'Type',
        account: 'Account',
        accounts: 'Accounts',
        allAccounts: 'All accounts',
        tags: 'Tags',
        allTags: 'All tags',
        categories: 'Categories',
        allCategories: 'All categories',
        groupBy: 'Group by',
        groupByNone: 'No grouping',
        groupByHint: 'Pick the breakdowns the report will draw.',
        generate: 'Generate report',
        accountSelectorTitle: 'Filter by account',
        // ReportCategoriesSheet (multi-select of report categories).
        categoriesModalTitle: 'Filter by category',
        categorySearchPlaceholder: 'Search category',
        noCategoryFound: 'No category found',
        // TYPE_OPTIONS (constant → typeOptions(t) function).
        typeExpense: 'Expenses',
        typeIncome: 'Income',
        typeAll: 'All',
        // GROUP_BY_ORDER → label per key (t(`custom.group.${group}`)).
        group: {
            category: 'Category',
            tag: 'Tag',
            account: 'Account',
            month: 'Month',
        },
        // Filter-row summary when more than one is selected. Literal numbers (no
        // plural) → their own placeholders.
        accountsSelected: '{{accountCount}} accounts',
        categoriesSelected: '{{catCount}} categories',
        tagsSelected: '{{tagCount}} tags',
        // Title of the exported report (PDF/CSV).
        title: {
            expense: 'Expenses',
            income: 'Income',
            all: 'Financial',
            accountsCount: '{{accountCount}} accounts',
            tagsCount: '{{tagCount}} tags',
            categoriesCount: '{{catCount}} categories',
            report: 'Report: {{parts}}',
        },
        summary: {
            totalExpense: 'Total expenses',
            expense: 'Expenses',
            totalIncome: 'Total income',
            income: 'Income',
            balance: 'Balance',
        },
        byAccount: 'By account',
        byTag: 'By tag',
        byCategory: 'By category',
        // "transactions" is fixed today (doesn't pluralize with 1) → {{txCount}}, not {{count}}.
        transactionsCount: '{{txCount}} transactions',
        subcategoriesOf: 'Subcategories of {{name}}',
        total: 'Total',
        moreCount: '+{{n}} more',
        monthlySummary: 'Monthly summary',
        monthCol: 'Month',
        incomeCol: 'Income',
        expenseCol: 'Expenses',
        balanceCol: 'Balance',
        totalCol: 'Total',
        transactionsTitle: 'Transactions ({{txCount}})',
        noCategory: 'No category',
        moreTransactions: '... and {{remaining}} more transactions',
        empty: 'No transactions found for the selected filters.',
        exportPdf: 'Export PDF',
        exportCsv: 'Export CSV',
        exportTitle: 'Export report',
        exportMessage: 'Choose the file format to share.',
        exportPdfDescription: 'A document ready to read, print or send.',
        exportCsvDescription: 'A spreadsheet for Excel or Google Sheets.',
    },

    // components/charts/ExpensePieChart.tsx
    pie: {
        defaultTitle: 'Expenses by category',
        total: 'Total',
        topCategory: 'Top category',
        noCategory: 'No category',
        share: 'Share',
        ofMonthTotal: 'of the month total',
        topWeight: 'Top categories',
        others: 'Other',
        othersGrouped: '+{{n}} grouped into Other',
        categoryCount_one: '{{count}} category in the period',
        categoryCount_other: '{{count}} categories in the period',
        expandSubcategories: 'View subcategories',
        collapseSubcategories: 'Collapse subcategories',
        subPctOfCategory: '{{pct}}% of category',
        pct: '{{pct}}%',
    },

    // Text generated in the PDF HTML (lib/reportTemplates.ts, lib/reportService.ts)
    // and in the spreadsheet/CSV content (lib/reportExport.ts, lib/reportService.ts).
    // Outside a component → i18n.t at generation time. Account/category/tag names
    // are user data and do NOT belong here (only labels and headers).
    pdf: {
        // PDF header and summary cards (reportTemplates).
        headerSubtitle: '{{start}} to {{end}} · {{count}} transactions',
        totalExpenses: 'Total expenses',
        totalIncome: 'Total income',
        income: 'Income',
        expenses: 'Expenses',
        balance: 'Balance',
        // PDF section titles.
        byAccount: 'By account',
        byTag: 'By tag',
        categories: 'Categories',
        subcategoriesOf: 'Subcategories of {{category}}',
        monthlySummary: 'Monthly summary',
        transactionsTitle: 'Transactions ({{count}})',
        // Column headers (reused by PDF and CSV).
        colAccount: 'Account',
        colTag: 'Tag',
        colCount: 'Qty',
        colSubcategory: 'Subcategory',
        colCategory: 'Category',
        colValue: 'Amount',
        colMonth: 'Month',
        colDate: 'Date',
        colDescription: 'Description',
        colTime: 'Time',
        colType: 'Type',
        colCard: 'Card',
        colNotes: 'Notes',
        colTags: 'Tags',
        // PDF footer and messages.
        moreTransactions: '... and {{count}} more transactions not listed',
        generatedAt: 'Generated on {{datetime}}',
        noData: 'No data in this period.',
        // Monthly spreadsheet by account (reportExport) — labels visible in the CSV.
        cardStatement: 'Card statement {{name}}',
        noAccount: 'No account',
        accountOrigin: 'Account / Source',
        total: 'Total',
        incomeSection: 'INCOME',
        expenseSection: 'EXPENSES',
        totalIncomeRow: 'Total income',
        totalExpensesRow: 'Total expenses',
        monthBalance: 'Monthly balance',
        cumulativeBalance: 'Cumulative balance',
        // Transaction type labels in the CSV.
        txTypeIncome: 'Income',
        txTypeExpense: 'Expense',
        txTypeTransfer: 'Transfer',
        txTypeAdjustment: 'Adjustment',
    },
} satisfies DeepPartialStrings<TranslationSchema['reports']>;
